// Ocean Insight Pro Mobile - Weather Details Screen
// Detailed weather information and forecasts

import React, { useEffect } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  useTheme,
  List,
  Divider,
  ActivityIndicator,
  Chip,
} from 'react-native-paper';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { useRoute, RouteProp } from '@react-navigation/native';
import { RootState, AppDispatch } from '../store';
import { RootStackParamList, WeatherAlert } from '../types';
import { MarineForecastBrief } from '../components/MarineForecastBrief';
import { fetchCurrentWeather, fetchHourlyWeatherForecast, fetchWeatherAlerts, fetchWeatherForecast } from '../store/weatherSlice';
import { hasMarineData } from '../utils/marineData';

type WeatherDetailsRouteProp = RouteProp<RootStackParamList, 'WeatherDetails'>;

export default function WeatherDetailsScreen() {
  const theme = useTheme();
  const route = useRoute<WeatherDetailsRouteProp>();
  const dispatch = useDispatch<AppDispatch>();
  const { location } = route.params;

  const { current: weatherData, forecast, hourlyForecast, alerts, loading } = useSelector((state: RootState) => state.weather);

  useEffect(() => {
    if (location) {
      dispatch(fetchCurrentWeather(location));
      dispatch(fetchWeatherForecast(location));
      dispatch(fetchHourlyWeatherForecast(location));
      dispatch(fetchWeatherAlerts(location));
    }
  }, [location, dispatch]);

  const onRefresh = () => {
    if (location) {
      dispatch(fetchCurrentWeather(location));
      dispatch(fetchWeatherForecast(location));
      dispatch(fetchHourlyWeatherForecast(location));
      dispatch(fetchWeatherAlerts(location));
    }
  };

  const getWindDirectionIcon = (degrees: number) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((degrees %= 360) < 0 ? degrees + 360 : degrees) / 45) % 8;
    return directions[index];
  };

  const getWeatherIcon = (conditions: string) => {
    if (conditions.includes('Clear')) return 'wb-sunny';
    if (conditions.includes('Cloudy')) return 'cloud';
    if (conditions.includes('Rain')) return 'grain';
    if (conditions.includes('Thunderstorm')) return 'flash-on';
    if (conditions.includes('Fog')) return 'blur-on';
    if (conditions.includes('Snow')) return 'ac-unit';
    return 'wb-cloudy';
  };

  const getUVIndexColor = (uvIndex: number) => {
    if (uvIndex <= 2) return '#4CAF50'; // Low - Green
    if (uvIndex <= 5) return '#FFEB3B'; // Moderate - Yellow
    if (uvIndex <= 7) return '#FF9800'; // High - Orange
    if (uvIndex <= 10) return '#F44336'; // Very High - Red
    return '#9C27B0'; // Extreme - Purple
  };

  const getUVIndexLabel = (uvIndex: number) => {
    if (uvIndex <= 2) return 'Low';
    if (uvIndex <= 5) return 'Moderate';
    if (uvIndex <= 7) return 'High';
    if (uvIndex <= 10) return 'Very High';
    return 'Extreme';
  };

  if (loading || !weatherData) {
    return (
      <View style={[styles.container, styles.centerContent, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" />
        <Paragraph style={{ marginTop: 16 }}>Loading weather details...</Paragraph>
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={onRefresh} />}
    >
      {/* Location Header */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.locationHeader}>
            <MaterialIcons name="location-on" size={24} color={theme.colors.primary} />
            <Title style={styles.locationTitle}>
              {location.latitude.toFixed(2)}°, {location.longitude.toFixed(2)}°
            </Title>
          </View>
          <Paragraph style={styles.timestamp}>
            Last updated: {new Date(weatherData.timestamp).toLocaleString()}
          </Paragraph>
        </Card.Content>
      </Card>

      {/* Current Conditions */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Current Conditions</Title>
          <View style={styles.conditionsHeader}>
            <MaterialIcons 
              name={getWeatherIcon(weatherData.weather.conditions)} 
              size={48} 
              color={theme.colors.primary} 
            />
            <View style={styles.mainConditions}>
              <Title style={styles.temperature}>{weatherData.weather.temperature}°F</Title>
              <Paragraph>{weatherData.weather.conditions}</Paragraph>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Wind Information */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Wind Conditions</Title>
          <List.Item
            title="Wind Speed"
            description={`${weatherData.wind.speed} knots`}
            left={() => <MaterialCommunityIcons name="weather-windy" size={24} color={theme.colors.primary} />}
          />
          <List.Item
            title="Wind Direction"
            description={`${weatherData.wind.direction}° (${getWindDirectionIcon(weatherData.wind.direction)})`}
            left={() => <MaterialCommunityIcons name="compass-outline" size={24} color={theme.colors.primary} />}
          />
          <List.Item
            title="Wind Gusts"
            description={weatherData.wind.gusts !== undefined ? `${weatherData.wind.gusts} knots` : 'Not reported'}
            left={() => <MaterialCommunityIcons name="weather-windy-variant" size={24} color={theme.colors.primary} />}
          />
          {weatherData.wind.source !== undefined && weatherData.wind.source !== '' && (
            <List.Item
              title="Wind Source"
              description={`${weatherData.wind.source}${weatherData.wind.stationDistanceKm !== undefined ? ` · ${weatherData.wind.stationDistanceKm} km away` : ''}${weatherData.wind.observedAt !== undefined && weatherData.wind.observedAt !== '' ? ` · observed ${new Date(weatherData.wind.observedAt).toLocaleString()}` : ''}`}
              left={() => <MaterialCommunityIcons name="database-check-outline" size={24} color={theme.colors.primary} />}
            />
          )}
        </Card.Content>
      </Card>

      {/* Wave Information */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Wave Conditions</Title>
          {hasMarineData(weatherData.waves) ? (
            <>
              <List.Item
                title="Significant Wave Height"
                description={`${weatherData.waves.height} feet`}
                left={() => <MaterialCommunityIcons name="waves" size={24} color={theme.colors.primary} />}
              />
              <List.Item
                title={`${weatherData.waves.periodType === 'dominant' ? 'Dominant' : weatherData.waves.periodType === 'peak' ? 'Peak' : 'Mean'} Wave Period`}
                description={weatherData.waves.period > 0 ? `${weatherData.waves.period} seconds` : 'Not reported'}
                left={() => <MaterialCommunityIcons name="timer-outline" size={24} color={theme.colors.primary} />}
              />
              <List.Item
                title="Wave Direction"
                description={`${weatherData.waves.direction}° (${getWindDirectionIcon(weatherData.waves.direction)})`}
                left={() => <MaterialCommunityIcons name="compass" size={24} color={theme.colors.primary} />}
              />
            </>
          ) : (
            <Paragraph style={styles.sectionSubtitle}>Marine model data is unavailable at this location or forecast hour. No wave value has been estimated.</Paragraph>
          )}
          {weatherData.waves.source !== undefined && weatherData.waves.source !== '' && (
            <List.Item
              title="Wave Source"
              description={`${weatherData.waves.source}${weatherData.waves.stationDistanceKm !== undefined ? ` · ${weatherData.waves.stationDistanceKm} km away` : ''}${weatherData.waves.observedAt !== undefined && weatherData.waves.observedAt !== '' ? ` · observed ${new Date(weatherData.waves.observedAt).toLocaleString()}` : ''}`}
              left={() => <MaterialCommunityIcons name="database-check-outline" size={24} color={theme.colors.primary} />}
            />
          )}
          {weatherData.waves.swellHeight !== undefined && (
            <>
              <Divider style={{ marginVertical: 8 }} />
              <Paragraph style={styles.sectionSubtitle}>Swell Conditions</Paragraph>
              <List.Item
                title="Swell Height"
                description={`${weatherData.waves.swellHeight} feet`}
                left={() => <MaterialCommunityIcons name="sine-wave" size={24} color={theme.colors.secondary} />}
              />
              {weatherData.waves.swellPeriod !== undefined && (
                <List.Item
                  title="Swell Period"
                  description={`${weatherData.waves.swellPeriod} seconds`}
                  left={() => <MaterialCommunityIcons name="timer" size={24} color={theme.colors.secondary} />}
                />
              )}
              {weatherData.waves.swellDirection !== undefined && (
                <List.Item
                  title="Swell Direction"
                  description={`${weatherData.waves.swellDirection}° (${getWindDirectionIcon(weatherData.waves.swellDirection)})`}
                  left={() => <MaterialCommunityIcons name="compass-rose" size={24} color={theme.colors.secondary} />}
                />
              )}
            </>
          )}
        </Card.Content>
      </Card>

      {/* Atmospheric Conditions */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Atmospheric Conditions</Title>
          <List.Item
            title="Humidity"
            description={`${weatherData.weather.humidity}%`}
            left={() => <MaterialCommunityIcons name="water-percent" size={24} color={theme.colors.primary} />}
          />
          <List.Item
            title="Pressure"
            description={`${weatherData.weather.pressure.toFixed(2)} inHg`}
            left={() => <MaterialCommunityIcons name="gauge" size={24} color={theme.colors.primary} />}
          />
          <List.Item
            title="Visibility"
            description={`${weatherData.weather.visibility} nautical miles`}
            left={() => <MaterialIcons name="visibility" size={24} color={theme.colors.primary} />}
          />
          <List.Item
            title="Precipitation"
            description={`${weatherData.weather.precipitation} mm`}
            left={() => <MaterialCommunityIcons name="weather-rainy" size={24} color={theme.colors.primary} />}
          />
        </Card.Content>
      </Card>

      {/* Weather Alerts */}
      {alerts && alerts.length > 0 && (
        <Card style={[styles.card, { backgroundColor: theme.colors.errorContainer }]}>
          <Card.Content>
            <Title style={{ color: theme.colors.onErrorContainer }}>
              <MaterialIcons name="warning" size={24} color={theme.colors.onErrorContainer} /> Weather Alerts
            </Title>
            <Divider style={{ marginVertical: 8, backgroundColor: `${theme.colors.onErrorContainer  }20` }} />
            {alerts.map((alert: WeatherAlert) => (
              <View key={alert.id} style={styles.alertItem}>
                <View style={styles.alertHeader}>
                  <Paragraph style={[styles.alertTitle, { color: theme.colors.onErrorContainer }]}>{alert.title}</Paragraph>
                  <Chip
                    mode="flat"
                    style={[styles.alertSeverityChip, { 
                      backgroundColor: alert.severity === 'Extreme' ? theme.colors.error :
                                     alert.severity === 'Severe' ? theme.colors.error :
                                     alert.severity === 'Moderate' ? theme.colors.warning :
                                     theme.colors.tertiary
                    }]}
                    textStyle={{ color: 'white', fontSize: 11 }}
                  >
                    {alert.severity}
                  </Chip>
                </View>
                <Paragraph style={[styles.alertTime, { color: theme.colors.onErrorContainer }]}>
                  {alert.event} • Issued {new Date(alert.startTime).toLocaleString()}
                </Paragraph>
                {alert.endTime && (
                  <Paragraph style={[styles.alertTime, { color: theme.colors.onErrorContainer }]}>
                    Until {new Date(alert.endTime).toLocaleString()}
                  </Paragraph>
                )}
                <Paragraph style={[styles.alertDescription, { color: theme.colors.onErrorContainer }]}>{alert.description}</Paragraph>
                {alert.sender && (
                  <Paragraph style={[styles.alertSender, { color: theme.colors.onErrorContainer }]}>— {alert.sender}</Paragraph>
                )}
                <Divider style={{ marginVertical: 12, backgroundColor: `${theme.colors.onErrorContainer  }20` }} />
              </View>
            ))}
          </Card.Content>
        </Card>
      )}

      <View style={styles.forecastBrief}>
        <MarineForecastBrief dailyForecast={forecast} hourlyForecast={hourlyForecast} />
      </View>

      {/* Sun & UV Information */}
      <Card style={styles.card}>
        <Card.Content>
          <Title>Sun & UV Information</Title>
          <List.Item
            title="UV Index"
            description={getUVIndexLabel(weatherData.uvIndex)}
            left={() => <MaterialCommunityIcons name="weather-sunny-alert" size={24} color={getUVIndexColor(weatherData.uvIndex)} />}
            right={() => (
              <Chip 
                mode="flat" 
                style={{ backgroundColor: getUVIndexColor(weatherData.uvIndex) }}
                textStyle={{ color: weatherData.uvIndex > 7 ? 'white' : 'black' }}
              >
                {weatherData.uvIndex}
              </Chip>
            )}
          />
          <List.Item
            title="Sunrise"
            description={new Date(weatherData.sunrise).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            left={() => <MaterialCommunityIcons name="weather-sunset-up" size={24} color={theme.colors.primary} />}
          />
          <List.Item
            title="Sunset"
            description={new Date(weatherData.sunset).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            left={() => <MaterialCommunityIcons name="weather-sunset-down" size={24} color={theme.colors.primary} />}
          />
        </Card.Content>
      </Card>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    margin: 16,
    marginBottom: 8,
    elevation: 2,
  },
  forecastBrief: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  locationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  locationTitle: {
    marginLeft: 8,
  },
  timestamp: {
    fontSize: 12,
    opacity: 0.6,
  },
  conditionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
  },
  mainConditions: {
    marginLeft: 16,
  },
  temperature: {
    fontSize: 36,
    lineHeight: 40,
  },
  sectionSubtitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    opacity: 0.8,
  },
  alertItem: {
    marginBottom: 8,
  },
  alertHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  alertTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    flex: 1,
    marginRight: 8,
  },
  alertSeverityChip: {
    height: 24,
  },
  alertTime: {
    fontSize: 12,
    opacity: 0.7,
    marginBottom: 4,
  },
  alertDescription: {
    fontSize: 14,
    marginTop: 8,
    lineHeight: 20,
  },
  alertSender: {
    fontSize: 12,
    opacity: 0.6,
    fontStyle: 'italic',
    marginTop: 8,
  },
});
